import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faInbox, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { Dialog } from "primereact/dialog";
import MobileCard from "./MobileCard";
import MobileCardPaginator from "./MobileCardPaginator";
import MobileFilterSheet, { MobileFilterButton } from "./MobileFilterSheet";
import MobileSortDropdown from "./MobileSortDropdown";
import { categorizeColumns } from "./mobileColumnConfig";
import "./mobile_card.scss";

const MobileCardList = ({
  value,
  columns,
  dataKey,
  tableRows,
  totalRows,
  onPageChange,
  onSort,
  filters,
  setFilters,
  buildFilterElement,
  clearFilter,
  filterCount,
  expandedRows,
  onRowExpand,
  onRowCollapse,
  rowExpansionTemplate,
  mobileExpansionTemplate,
  mobileFilterContent,
  selectionBody,
  mobileExpansionLabel,
  showGlobalSearch,
  globalFilterValue,
  onGlobalFilterChange,
  onGlobalFilterClear,
  paginator,
  headerButtonsLeft,
  headerButtonsRight,
  headerIcons,
  simpleTable,
  onFilterSheetOpen,
  onFilterSheetApply,
}) => {
  const { t } = useTranslation("translations");
  const [filterSheetVisible, setFilterSheetVisible] = useState(false);

  const categorized = useMemo(() => categorizeColumns(columns), [columns]);

  const data = useMemo(() => value || [], [value]);

  const hasExpansion = !!(rowExpansionTemplate || mobileExpansionTemplate);

  const getRowId = useCallback(
    (row) => (dataKey ? row?.[dataKey] : row?.id),
    [dataKey]
  );

  const isRowExpanded = useCallback(
    (row) => {
      if (!expandedRows || !row) return false;
      const rowId = getRowId(row);
      if (Array.isArray(expandedRows)) {
        return expandedRows.some((r) => getRowId(r) === rowId);
      }
      return !!expandedRows[rowId];
    },
    [expandedRows, getRowId]
  );

  // Find the currently expanded row (for the dialog)
  const expandedRowData = useMemo(() => {
    if (!expandedRows || !data.length) return null;
    if (Array.isArray(expandedRows)) {
      for (const row of data) {
        if (expandedRows.some((r) => getRowId(r) === getRowId(row))) {
          return row;
        }
      }
    }
    return null;
  }, [expandedRows, data, getRowId]);

  const handleExpandClick = useCallback(
    (rowData) => {
      if (isRowExpanded(rowData)) {
        onRowCollapse?.({ data: rowData });
      } else {
        onRowExpand?.({ data: rowData });
      }
    },
    [isRowExpanded, onRowExpand, onRowCollapse]
  );

  const handleDialogHide = useCallback(() => {
    if (expandedRowData) {
      onRowCollapse?.({ data: expandedRowData });
    }
  }, [expandedRowData, onRowCollapse]);

  // Get title for the dialog header
  const getRowTitle = (row) => {
    if (!row || !categorized.title) return "";
    if (categorized.title.body) {
      try {
        return categorized.title.body(row);
      } catch {
        return row[categorized.title.field] ?? "";
      }
    }
    return row[categorized.title.field] ?? "";
  };

  // Get subtitle info for dialog header
  const getRowSubtitle = (row) => {
    if (!row) return [];
    const identityFields = categorized.primary.filter(
      (c) => c.group === "identity" || c.group === "dates"
    );
    return identityFields.slice(0, 2).map((col) => {
      if (col.body) {
        try {
          return col.body(row);
        } catch {
          return row[col.field] ?? "";
        }
      }
      return row[col.field] ?? "";
    });
  };

  const expansionDialogHeader = expandedRowData ? (
    <div className="mobile-expansion-dialog__header">
      <FontAwesomeIcon icon={faLayerGroup} className="mobile-expansion-dialog__header-icon" />
      <div className="mobile-expansion-dialog__header-text">
        <div className="mobile-expansion-dialog__header-title">
          {getRowTitle(expandedRowData)}
        </div>
        {getRowSubtitle(expandedRowData).length > 0 && (
          <div className="mobile-expansion-dialog__header-subtitle">
            {getRowSubtitle(expandedRowData).map((val, i) => (
              <span key={i}>{val}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="mobile-card-list">
      {/* Sticky Header */}
      <div className="mobile-card-list__header">
        <div className="mobile-card-list__controls">
          {showGlobalSearch && (
            <div className="mobile-card-list__search">
              <i className="pi pi-search mobile-card-list__search-icon" />
              <input
                value={globalFilterValue || ""}
                onChange={(e) => onGlobalFilterChange?.(e.target.value)}
                placeholder={t("common.search", "Search...")}
                className="mobile-card-list__search-input"
              />
              {globalFilterValue?.length > 0 && (
                <div
                  className="mobile-card-list__search-clear"
                  onClick={onGlobalFilterClear}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              )}
            </div>
          )}

          <div className="mobile-card-list__toolbar">
            {headerIcons && (
              <div className="mobile-card-list__export">
                {headerIcons}
              </div>
            )}
            {categorized.sortable.length > 0 && (
              <MobileSortDropdown
                sortableColumns={categorized.sortable}
                tableRows={tableRows}
                onSort={onSort}
                t={t}
              />
            )}
            {(categorized.filterable.length > 0 || mobileFilterContent) && (
              <MobileFilterButton
                filterCount={filterCount}
                onClick={() => { setFilterSheetVisible(true); if (onFilterSheetOpen) onFilterSheetOpen(); }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Card List */}
      <div className="mobile-card-list__cards">
        {data.length === 0 ? (
          <div className="mobile-card-list__empty">
            <FontAwesomeIcon
              icon={faInbox}
              className="mobile-card-list__empty-icon"
            />
            <div className="mobile-card-list__empty-text">
              {t("common.noResults", "No results found")}
            </div>
            <div className="mobile-card-list__empty-subtext">
              {t("common.tryAdjustingFilters", "Try adjusting your filters or search")}
            </div>
          </div>
        ) : (
          data.map((row, index) => (
            <MobileCard
              key={dataKey ? row[dataKey] : index}
              rowData={row}
              categorized={categorized}
              isExpanded={isRowExpanded(row)}
              onExpandClick={handleExpandClick}
              hasExpansion={hasExpansion}
              selectionBody={selectionBody}
              expansionLabel={mobileExpansionLabel}
              t={t}
            />
          ))
        )}
      </div>

      {/* Paginator */}
      {paginator && data.length > 0 && (
        <MobileCardPaginator
          tableRows={tableRows}
          totalRows={totalRows}
          onPageChange={onPageChange}
          t={t}
        />
      )}

      {/* Unified Filter Bottom Sheet */}
      <MobileFilterSheet
        visible={filterSheetVisible}
        onHide={() => { setFilterSheetVisible(false); if (onFilterSheetApply) onFilterSheetApply({ skipRemote: true }); }}
        onApply={() => {
          setFilterSheetVisible(false);
          if (onFilterSheetApply) onFilterSheetApply();
        }}
        onClearAndClose={() => {
          setFilterSheetVisible(false);
          if (onFilterSheetApply) onFilterSheetApply({ skipRemote: true });
          clearFilter();
        }}
        filterableColumns={categorized.filterable}
        buildFilterElement={buildFilterElement}
        filters={filters}
        setFilters={setFilters}
        filterCount={filterCount}
        mobileFilterContent={mobileFilterContent}
        simpleTable={simpleTable}
        t={t}
      />

      {/* Expansion Bottom Sheet Dialog */}
      {hasExpansion && (
        <Dialog
          visible={!!expandedRowData}
          onHide={handleDialogHide}
          header={expansionDialogHeader}
          position="bottom"
          modal
          draggable={false}
          resizable={false}
          className="mobile-expansion-dialog"
          style={{ width: "100vw", margin: 0 }}
          contentStyle={{ padding: 0 }}
        >
          <div className="mobile-expansion-dialog__body">
            {expandedRowData && (
              mobileExpansionTemplate
                ? mobileExpansionTemplate(expandedRowData)
                : rowExpansionTemplate
                  ? rowExpansionTemplate(expandedRowData)
                  : null
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default MobileCardList;
